import { useEffect, useMemo, useState } from 'react'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import type {
  CustomFieldSchema,
  PostType,
} from '@tribeplatform/gql-client/types'
import {
  useNetwork,
  useReorderPostTypeField,
} from '@tribeplatform/react-sdk/hooks'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { noop } from '@tribeplatform/react-ui-kit/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useAdminPanel } from '../../AdminPanel/index.js'
import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { FieldSortableItemVariant } from '../../Views/components/FieldSortableItem.js'
import { FieldsSortableList } from '../../Views/components/FieldsSortableList.js'
import { GenericSettings } from '../../Views/components/GenericSettings.js'
import { AddNewField } from '../components/AddNewField.js'
import { FieldOptions } from '../components/FieldOptions.js'
import { FieldsAccordionButton } from '../components/FieldsAccordionButton.js'
import { FieldSubTypeIcon } from '../components/FieldSubTypeIcon.js'
import type { CMSSharedState, FieldItem } from '../types.js'
import { defaultInitialSingleField } from '../utils/initialFieldSchemas.js'
import {
  convertToCustomFields,
  isFieldLocked,
  isFieldPrivate,
} from '../utils/utils.js'

import { FieldSettings } from './FieldSettings.js'

interface Props {
  postType: PostType
  goBack: () => void
}
export const FieldsSettings = ({ postType, goBack }: Props) => {
  const { $t } = useI18n()
  const { setSharedState, resetSharedState } = useAdminPanel<CMSSharedState>()
  const [selectedField, setSelectedField] = useState<CustomFieldSchema>(null)
  const [showFieldSettings, setShowFieldSettings] = useState<boolean>(false)
  const [showAddNewField, setShowAddNewField] = useState(false)
  const { data: network } = useNetwork()
  const fields = useMemo(() => {
    return postType?.postFields?.fields ?? []
  }, [postType?.postFields?.fields])
  const [activeFields, setActiveFields] = useState(() =>
    fields.filter(field => !field.archived),
  )
  const archivedFields = useMemo(
    () => fields.filter(field => !!field.archived),
    [fields],
  )
  const activeFieldItems = useMemo(
    () =>
      activeFields.map<FieldItem & CustomFieldSchema>(field => {
        const fieldItem: FieldItem = {
          id: field.key,
          label: field.name,
          icon: <FieldSubTypeIcon field={field} />,
          isPrivate: isFieldPrivate(field?.readPrivacy),
          isLocked: isFieldLocked(field.key),
          enabled: true,
          draggable: true,
        }

        return {
          ...fieldItem,
          ...field,
        }
      }),
    [activeFields],
  )
  const archivedFieldItems = useMemo(
    () =>
      archivedFields.map<FieldItem & CustomFieldSchema>(field => {
        const fieldItem: FieldItem = {
          id: field.key,
          label: field.name,
          icon: <FieldSubTypeIcon field={field} />,
          isPrivate: isFieldPrivate(field?.readPrivacy),
          enabled: true,
          draggable: false,
          isLocked: false,
        }
        return {
          ...fieldItem,
          ...field,
        }
      }),
    [archivedFields],
  )
  const [canAddPostTypeField] = hasScopesPermission(network, [
    'addPostTypeField',
  ])
  const { mutateAsync: reorderFields } = useReorderPostTypeField()

  useEffect(() => {
    setActiveFields(fields.filter(field => !field.archived))
  }, [fields])

  const handleReorder = async (newItems: CustomFieldSchema[]) => {
    const input = newItems.map(({ key }, index) => ({
      index,
      key,
    }))
    setActiveFields(newItems)

    try {
      await reorderFields({
        id: postType.id,
        input,
      })
    } catch (error) {
      toast({
        title: $t({
          defaultMessage: 'Error',
          id: 'Generics.Error',
        }),
        description: $t({
          defaultMessage: 'An error occurred while reordering the fields.',
          id: 'Admin.CMSFields.Reorder.Error',
        }),
        status: 'error',
      })
    }
  }

  return (
    <>
      <GenericSettings
        title={$t({
          defaultMessage: 'Fields',
          id: 'Generics.Fields',
        })}
        goBack={goBack}
      >
        <Accordion variant="chevron-down" defaultOpen>
          <Accordion.Button>
            <FieldsAccordionButton
              canAddField={canAddPostTypeField}
              onAddField={() => {
                setShowAddNewField(true)
                setSharedState({
                  newField: defaultInitialSingleField,
                })
              }}
            />
          </Accordion.Button>
          <Accordion.Panel>
            <FieldsSortableList
              items={activeFieldItems}
              onReorder={async newItems => {
                await handleReorder(convertToCustomFields(newItems))
              }}
              onClick={fieldId => {
                const field = activeFieldItems.find(({ id }) => id === fieldId)
                setShowFieldSettings(true)
                setSharedState({
                  selectedField: field,
                })
                setSelectedField(field)
              }}
              variant={FieldSortableItemVariant.DOTS}
              onToggle={noop}
              onActivate={id => {
                setSharedState({ highlightedFieldId: id })
              }}
              hasArrow={false}
              renderDropDownItems={fieldId => {
                if (isFieldLocked(fieldId)) {
                  return null
                }

                return (
                  <FieldOptions
                    fieldId={fieldId}
                    postType={postType}
                    openManageFieldModal={() => {
                      const field = activeFieldItems.find(
                        field => field.id === fieldId,
                      )
                      setShowFieldSettings(true)
                      setSharedState({
                        selectedField: field,
                      })
                      setSelectedField(field)
                    }}
                  />
                )
              }}
            />
            {activeFieldItems.length === 0 && (
              <p className="mb-5">
                <T
                  defaultMessage="You have not added any fields yet."
                  id="Admin.CMSFields.NoFieldsAdded"
                />
              </p>
            )}
            <Button
              variant="secondaryNeutral"
              onClick={() => {
                setShowAddNewField(true)
                setSharedState({
                  newField: defaultInitialSingleField,
                })
              }}
              fullWidth
              leadingIcon={<SvgIcon name="plus" />}
              className="mt-2"
            >
              <T defaultMessage="Add field" id="Admin.CMSFields.AddField" />
            </Button>
          </Accordion.Panel>
        </Accordion>
        {archivedFieldItems.length !== 0 && (
          <Accordion variant="chevron-down">
            <Accordion.Button>
              <T
                defaultMessage="Archived fields"
                id="Admin.CMSFields.ArchivedFields"
              />
            </Accordion.Button>
            <Accordion.Panel>
              <FieldsSortableList
                items={archivedFieldItems}
                onReorder={noop}
                onClick={fieldId => {
                  const field = archivedFieldItems.find(
                    ({ id }) => id === fieldId,
                  )
                  setShowFieldSettings(true)
                  setSharedState({
                    selectedField: field,
                  })
                  setSelectedField(field)
                }}
                variant={FieldSortableItemVariant.DOTS}
                onToggle={noop}
                hasArrow={false}
                renderDropDownItems={id => (
                  <FieldOptions
                    fieldId={id}
                    postType={postType}
                    openManageFieldModal={() => {
                      const field = archivedFieldItems.find(
                        field => field.id === id,
                      )
                      setShowFieldSettings(true)
                      setSharedState({
                        selectedField: field,
                      })
                      setSelectedField(field)
                    }}
                  />
                )}
              />
            </Accordion.Panel>
          </Accordion>
        )}
      </GenericSettings>
      {showFieldSettings && (
        <FieldSettings
          field={selectedField}
          setField={newField => {
            setSharedState({
              selectedField: newField,
            })
            setSelectedField(newField)
          }}
          isLocked={isFieldLocked(selectedField?.key)}
          postTypeId={postType.id}
          goBack={() => {
            resetSharedState()
            setShowFieldSettings(false)
            setSelectedField(null)
          }}
        />
      )}
      {showAddNewField && (
        <AddNewField
          postTypeId={postType.id}
          goBack={() => {
            resetSharedState()
            setShowAddNewField(false)
          }}
        />
      )}
    </>
  )
}
